import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminCampaignDetail, AdminCampaignRun } from '@app/Campaigns/types';
import { getCampaign, recomputeMeasuredReach, rescheduleRun } from '@app/Campaigns/campaigns.api';
import { CampaignStateBadge, RunStateBadge, ApprovalBadge, Pill } from '@app/Campaigns/badges';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '11px 14px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13.5,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};

function km(m: number | null): string {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
}
function pct(p: number | null): string {
  if (p == null) return '—';
  return `${Math.round(p)}%`;
}
function fmtInt(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString();
}
// Lenient parse of the backend measuredReach JSON string column; never throws.
function safeParse(json: string | null | undefined): any {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
function confidenceColor(score: number): string {
  return score >= 0.7 ? colors.success : score >= 0.4 ? colors.warning : colors.danger;
}
// A run can move while it hasn't started and the driver hasn't declined/cancelled.
function isReschedulable(r: AdminCampaignRun): boolean {
  return r.state === 'PENDING' && (r.approvalState === 'PENDING_APPROVAL' || r.approvalState === 'APPROVED');
}
// ISO instant -> "YYYY-MM-DDTHH:mm" in the admin's local timezone, for <input type="datetime-local">.
function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// "high_income" -> "High income"
function formatCohortKey(key: string): string {
  return key
    .split('_')
    .join(' ')
    .replace(/^\w/, c => c.toUpperCase());
}

export const CampaignDetailPage: React.FunctionComponent = () => {
  const { campaignId } = useParams();
  const id = Number(campaignId);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminCampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reachBusy, setReachBusy] = useState(false);
  const [editRunId, setEditRunId] = useState<number | null>(null);
  const [draftSlot, setDraftSlot] = useState('');
  const [rescheduleBusy, setRescheduleBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getCampaign(id);
      setDetail(d);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Recompute is fire-and-forget: it starts workflows; results arrive later, so we
  // report the tally and let the admin Refresh once the worker has written them back.
  const onRecompute = async () => {
    setReachBusy(true);
    try {
      const r = await recomputeMeasuredReach(id);
      toast(
        `Recompute started: ${r.triggered} run(s) triggered` +
          (r.skippedNoGps ? `, ${r.skippedNoGps} skipped (no GPS)` : '') +
          (r.failed ? `, ${r.failed} failed` : '')
      );
    } catch (e: any) {
      toast.error(e?.message || 'Failed to recompute measured reach');
    } finally {
      setReachBusy(false);
    }
  };

  const onSaveReschedule = async (runId: number) => {
    if (!draftSlot) return;
    const slot = new Date(draftSlot);
    if (slot.getMinutes() !== 0 && slot.getMinutes() !== 30) {
      toast.error('Slot must start on a :00 or :30 boundary.');
      return;
    }
    setRescheduleBusy(true);
    try {
      const r = await rescheduleRun(id, runId, slot.toISOString());
      toast(
        `Run rescheduled to ${formatDateTime(r.slotStart)} — driver ${r.assignedDriverDisplayName || 'auto-assigned'} (pending re-approval)`
      );
      setEditRunId(null);
      setDraftSlot('');
      await reload();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to reschedule run');
    } finally {
      setRescheduleBusy(false);
    }
  };

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: colors.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 16 },
    headerRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 },
    title: { fontSize: 24, fontWeight: 800, color: colors.title, margin: 0 },
    sub: { fontSize: 13, color: colors.subtle, marginBottom: 20 },
    statRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 },
    stat: { flex: '1 1 130px', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: '14px 16px' },
    statLabel: { fontSize: 12, fontWeight: 600, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px' },
    statValue: { fontSize: 22, fontWeight: 800, color: colors.title, marginTop: 4 },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: colors.title, margin: '0 0 12px' },
    metaGrid: { display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 22, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: 18 },
    mrCard: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: 18, marginBottom: 22 },
    mrHeader: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
    metricLabel: { fontSize: 12, fontWeight: 700, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px', margin: '0 0 8px', cursor: 'help' },
    btnPrimary: { padding: '8px 16px', borderRadius: radii.button, border: `1px solid ${colors.accent}`, background: colors.accent, color: '#fff', fontSize: 13, fontWeight: 600 },
    btnGhost: { padding: '8px 14px', borderRadius: radii.button, border: `1px solid ${colors.border}`, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600 }
  };

  const meta = (label: string, value: React.ReactNode) => (
    <div>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ fontSize: 14, color: colors.body, marginTop: 4, fontWeight: 500 }}>{value}</div>
    </div>
  );
  const stat = (label: string, value: React.ReactNode) => (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/campaigns')}>
          <ArrowLeftIcon /> Back to Campaigns
        </button>
        <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading campaign…</div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/campaigns')}>
          <ArrowLeftIcon /> Back to Campaigns
        </button>
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 12, padding: 20 }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const s = detail.summary;
  const runs = detail.runs;
  const mr = safeParse(s.measuredReach);

  return (
    <div style={styles.wrap}>
      <button style={styles.back} onClick={() => navigate('/campaigns')}>
        <ArrowLeftIcon /> Back to Campaigns
      </button>

      <div style={styles.headerRow}>
        <h1 style={styles.title}>{s.name || `Campaign #${s.campaignId}`}</h1>
        <CampaignStateBadge state={s.currentState} />
        {s.type && <Pill text={s.type} tone="blue" />}
      </div>
      <div style={styles.sub}>id {s.campaignId}</div>

      <div style={styles.metaGrid}>
        {meta('Owner', s.ownerName || s.ownerEmail || '—')}
        {meta('Owner email', s.ownerEmail || '—')}
        {meta('Ad', s.adName || '—')}
        {meta('Generation', s.generationStatus || '—')}
        {meta('Created', formatDateTime(s.createDateTime))}
      </div>

      <div style={styles.statRow}>
        {stat('Total runs', s.runsTotal)}
        {stat('In progress', s.runsInProgress)}
        {stat('Paused', s.runsPaused)}
        {stat('Pending approval', s.runsPendingApproval)}
        {stat('Completed', s.runsCompleted)}
      </div>

      <div style={styles.mrCard}>
        <div style={styles.mrHeader}>
          <h2 style={{ ...styles.sectionTitle, margin: 0, flex: 1 }}>Measured Reach</h2>
          <button
            onClick={() => reload()}
            disabled={loading}
            style={{ ...styles.btnGhost, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            Refresh
          </button>
          <button
            onClick={onRecompute}
            disabled={reachBusy}
            style={{ ...styles.btnPrimary, cursor: reachBusy ? 'default' : 'pointer', opacity: reachBusy ? 0.7 : 1 }}
          >
            {reachBusy ? 'Starting…' : 'Recompute measured reach'}
          </button>
        </div>
        {mr ? (
          <>
            <div
              style={styles.metricLabel}
              title="Vehicle exposures: how many vehicles were physically near the ad while it was visible, from AADT × dwell. Repeat passes over a road count again (impressions, not unique people)."
            >
              Co-presence · vehicle exposures
            </div>
            <div style={styles.statRow}>
              {stat('Total impressions', fmtInt(mr.total_impressions))}
              {mr.cohort_impressions &&
                Object.entries(mr.cohort_impressions).map(([k, v]) => (
                  <React.Fragment key={k}>{stat(formatCohortKey(k), fmtInt(Number(v)))}</React.Fragment>
                ))}
              {mr.distance_m != null && stat('Distance', km(mr.distance_m))}
              {mr.total_dwell_hours != null && stat('Dwell', `${mr.total_dwell_hours.toFixed(1)} hrs`)}
              {mr.confidence_score != null &&
                stat(
                  'Confidence',
                  <span>
                    <span style={{ color: confidenceColor(mr.confidence_score) }}>
                      {(mr.confidence_score * 100).toFixed(0)}%
                    </span>
                    {mr.confidence_label && (
                      <span style={{ color: colors.subtle, fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>
                        {' '}
                        {mr.confidence_label}
                      </span>
                    )}
                  </span>
                )}
              {mr.run_count != null && stat('Runs analyzed', mr.run_count)}
            </div>

            {(mr.population_along_route != null || mr.cohort_population) && (
              <>
                <div
                  style={styles.metricLabel}
                  title={
                    'Unique residents living near the route, counted once (a road driven twice is not double-counted within a run). ' +
                    'Apportioned from Census block-group population inside the buffer. "High income" = residents in top-quartile-income areas. ' +
                    'Note: per-run figures are summed at the campaign level, so residents covered by more than one run can be counted more than once across runs.'
                  }
                >
                  Population along route · unique residents
                  {mr.population_buffer_m ? ` within ${Math.round(mr.population_buffer_m)} m` : ''}
                  {mr.population_bg_count != null ? ` · ${fmtInt(mr.population_bg_count)} block groups` : ''}
                </div>
                <div style={styles.statRow}>
                  {stat('Residents reached', fmtInt(mr.population_along_route))}
                  {mr.cohort_population &&
                    Object.entries(mr.cohort_population).map(([k, v]) => (
                      <React.Fragment key={`pop-${k}`}>{stat(formatCohortKey(k), fmtInt(Number(v)))}</React.Fragment>
                    ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{ color: colors.subtle, fontSize: 13.5 }}>
            Not computed yet — click “Recompute measured reach” to (re)run the analysis. Results are computed
            asynchronously; use Refresh once the worker has finished.
          </div>
        )}
      </div>

      <h2 style={styles.sectionTitle}>Runs ({runs.length})</h2>
      <div style={styles.card}>
        {runs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.subtle }}>No runs booked for this campaign yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead>
                <tr>
                  <th style={th}>Slot start</th>
                  <th style={th}>State</th>
                  <th style={th}>Approval</th>
                  <th style={th}>Driver</th>
                  <th style={th}>Progress</th>
                  <th style={th}>Distance</th>
                  <th style={th}>Measured reach</th>
                  <th style={th}>Pause</th>
                  <th style={th}>Booked</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.runId}>
                    <td style={td} title={formatDateTime(r.scheduledStartAt)}>{formatDateTime(r.scheduledStartAt)}</td>
                    <td style={td}>
                      <RunStateBadge state={r.state} />
                    </td>
                    <td style={td}>
                      <ApprovalBadge state={r.approvalState} />
                    </td>
                    <td style={td}>{r.driverName || <span style={{ color: colors.placeholder }}>—</span>}</td>
                    <td style={td}>{pct(r.progressPercentage)}</td>
                    <td style={td}>{km(r.distanceTraveledMeters)}</td>
                    <td style={td}>
                      {(() => {
                        const rmr = safeParse(r.measuredReach);
                        if (!rmr) return <span style={{ color: colors.placeholder }}>—</span>;
                        return (
                          <div>
                            <span title="Co-presence — vehicle exposures (impressions)">
                              {fmtInt(rmr.total_impressions)}
                              {rmr.confidence_score != null && (
                                <span style={{ color: confidenceColor(rmr.confidence_score), marginLeft: 8, fontSize: 12 }}>
                                  {(rmr.confidence_score * 100).toFixed(0)}%
                                </span>
                              )}
                            </span>
                            {rmr.population_along_route != null && (
                              <div
                                style={{ color: colors.subtle, fontSize: 12, marginTop: 2 }}
                                title="Population along route — unique residents near this run"
                              >
                                {fmtInt(rmr.population_along_route)} residents
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={td}>
                      {r.pauseReason ? <Pill text={r.pauseReason} tone="amber" /> : <span style={{ color: colors.placeholder }}>—</span>}
                    </td>
                    <td style={td}>{relativeTime(r.createdAt)}</td>
                    <td style={td}>
                      {!isReschedulable(r) ? (
                        <span style={{ color: colors.placeholder }}>—</span>
                      ) : editRunId === r.runId ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="datetime-local"
                            step={1800}
                            value={draftSlot}
                            onChange={e => setDraftSlot(e.target.value)}
                            disabled={rescheduleBusy}
                            style={{
                              padding: '6px 8px',
                              borderRadius: radii.button,
                              border: `1px solid ${colors.border}`,
                              fontSize: 13,
                              color: colors.body
                            }}
                          />
                          <button
                            onClick={() => onSaveReschedule(r.runId)}
                            disabled={rescheduleBusy || !draftSlot}
                            style={{
                              ...styles.btnPrimary,
                              padding: '6px 12px',
                              cursor: rescheduleBusy ? 'default' : 'pointer',
                              opacity: rescheduleBusy || !draftSlot ? 0.7 : 1
                            }}
                          >
                            {rescheduleBusy ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setEditRunId(null);
                              setDraftSlot('');
                            }}
                            disabled={rescheduleBusy}
                            style={{ ...styles.btnGhost, padding: '6px 12px', cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            setEditRunId(r.runId);
                            setDraftSlot(toLocalInputValue(r.scheduledStartAt));
                          }}
                          style={{ ...styles.btnGhost, padding: '6px 12px', cursor: 'pointer' }}
                        >
                          Reschedule
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
